/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { FC, useState, useEffect, useMemo } from 'react'
import axios from 'axios'
import { API_URL } from '../../../modules/auth/core/_requests'
import { useAuth } from '../../../modules/auth'
import AIOptions, { AIOptionValues, defaultAIOptionValues } from './components/AIOptions'

interface ProductData {
  id: number
  title: string
  description: string
  variants: any[]
  images: string[]
}

interface BlogStatsData {
  storeInfo: {
    name: string
    shopUrl: string
    hasBlogConfigured: boolean
    blogManualsId?: string
  }
  blogStats: {
    totalArticles: number
    lastChecked?: string
    error?: string
    details?: any
  }
}

export const ManualWriterPage: FC = () => {
  const { currentUser } = useAuth()
  const currentStoreName = currentUser?.decoded?.current_shopify_store || ''

  // Form fields
  const [productUrl, setProductUrl] = useState('')
  const [additionalContent, setAdditionalContent] = useState('')
  const [aiOptions, setAiOptions] = useState<AIOptionValues>(defaultAIOptionValues)

  // Product data
  const [productData, setProductData] = useState<ProductData | null>(null)
  
  // Generated content
  const [extractedInstructions, setExtractedInstructions] = useState('')
  const [generatedManual, setGeneratedManual] = useState('')
  const [editedManual, setEditedManual] = useState('')

  // Loading states
  const [loading, setLoading] = useState(false)
  const [fetchingProduct, setFetchingProduct] = useState(false)
  const [extractingInstructions, setExtractingInstructions] = useState(false)
  const [generatingManual, setGeneratingManual] = useState(false)

  // Blog stats
  const [blogStats, setBlogStats] = useState<BlogStatsData | null>(null)
  const [loadingStats, setLoadingStats] = useState(false)

  // Alerts and notifications
  const [hasExistingManual, setHasExistingManual] = useState(false)
  const [existingManualUrl, setExistingManualUrl] = useState('')

  // Fetch blog stats function
  const fetchBlogStats = async () => {
    if (!currentStoreName) return
    
    setLoadingStats(true)
    try {
      const response = await axios.get<BlogStatsData>(`${API_URL}/api/shopify_manual_writer/blog-stats/${currentStoreName}`)
      setBlogStats(response.data)
    } catch (error) {
      console.error('Failed to fetch blog stats:', error)
    } finally {
      setLoadingStats(false)
    }
  }

  // Fetch blog stats on component mount
  useEffect(() => {
    fetchBlogStats()
  }, [currentStoreName])

  // Fetch product data from URL
  const handleFetchProduct = async () => {
    if (!productUrl.trim()) {
      alert('Please enter a valid product URL.')
      return
    }

    setFetchingProduct(true)
    try {
      const response = await axios.post(`${API_URL}/api/shopify_manual_writer/fetch-product`, {
        productUrl: productUrl.trim(),
      })
      
      const product = response.data.product
      setProductData(product)

      // Check if product already has a manual
      if (currentStoreName && product.id) {
        try {
          const manualCheckResponse = await axios.post(`${API_URL}/api/shopify_manual_writer/check-existing-manual`, {
            storeName: currentStoreName,
            productId: product.id,
          })
          
          setHasExistingManual(manualCheckResponse.data.hasExistingManual)
          setExistingManualUrl(manualCheckResponse.data.manualUrl || '')
          
          if (manualCheckResponse.data.hasExistingManual) {
            alert('This product already has a manual.')
          }
        } catch (error) {
          console.error('Failed to check existing manual:', error)
        }
      }

      // Extract instructions from product description
      if (product.description) {
        await handleExtractInstructions(product.description)
      }

      // Auto-generate manual
      await handleGenerateManual(product)

    } catch (error: any) {
      console.error('Failed to fetch product:', error)
      alert('Failed to fetch product data.')
    } finally {
      setFetchingProduct(false)
    }
  }

  // Extract instructions from description
  const handleExtractInstructions = async (description?: string) => {
    const desc = description || productData?.description || ''
    if (!desc.trim()) return

    setExtractingInstructions(true)
    try {
      const response = await axios.post(`${API_URL}/api/shopify_manual_writer/extract-instructions`, {
        description: desc,
        ...aiOptions,
      })
      setExtractedInstructions(response.data.instructions)
    } catch (error: any) {
      console.error('Failed to extract instructions:', error)
      alert('Failed to extract instructions.')
    } finally {
      setExtractingInstructions(false)
    }
  }

  // Generate manual content
  const handleGenerateManual = async (product?: ProductData) => {
    const prod = product || productData
    if (!prod || !currentStoreName) {
      alert('Please fetch product data first.')
      return
    }

    setGeneratingManual(true)
    try {
      const response = await axios.post(`${API_URL}/api/shopify_manual_writer/generate-manual`, {
        storeName: currentStoreName,
        title: prod.title,
        description: prod.description,
        additionalContent: additionalContent.trim(),
        imageUrl: prod.images.length > 0 ? prod.images[0] : '',
        ...aiOptions,
      })
      
      setGeneratedManual(response.data.manualContent)
      setEditedManual(response.data.manualContent)
    } catch (error: any) {
      console.error('Failed to generate manual:', error)
      alert('Failed to generate manual.')
    } finally {
      setGeneratingManual(false)
    }
  }

  // Push manual to Shopify
  const handlePushManual = async () => {
    if (!productData || !editedManual.trim() || !currentStoreName) {
      alert('Please ensure you have product data and generated manual.')
      return
    }

    setLoading(true)
    try {
      const manualTitle = `${productData.title} Manual`
      
      await axios.post(`${API_URL}/api/shopify_manual_writer/push-manual`, {
        storeName: currentStoreName,
        title: manualTitle,
        manualContent: editedManual.trim(),
        productId: productData.id,
        featuredImageUrl: productData.images.length > 0 ? productData.images[0] : '',
      })

      alert('Manual successfully pushed to Shopify!')
      
      // Refresh blog stats to show updated count
      fetchBlogStats()
      
      // Reset form
      setProductUrl('')
      setAdditionalContent('')
      setProductData(null)
      setExtractedInstructions('')
      setGeneratedManual('')
      setEditedManual('')
      setHasExistingManual(false)
      setExistingManualUrl('')
      
    } catch (error: any) {
      console.error('Failed to push manual:', error)
      const errorMessage = error.response?.data?.details || error.response?.data?.error || 'Failed to push manual to Shopify.'
      alert(`Error: ${errorMessage}`)
    } finally {
      setLoading(false)
    }
  }

  // Reset all form data
  const handleReset = () => {
    setProductUrl('')
    setAdditionalContent('')
    setProductData(null)
    setExtractedInstructions('')
    setGeneratedManual('')
    setEditedManual('')
    setHasExistingManual(false)
    setExistingManualUrl('')
  }

  // Calculate manual statistics
  const stats = useMemo(() => {
    const strippedText = editedManual.replace(/<[^>]+>/g, ' ').trim()
    const characters = strippedText.length
    const letters = (strippedText.match(/[a-zA-Z]/g) || []).length
    const wordsArray = strippedText.split(/\s+/).filter(Boolean)
    const words = wordsArray.length
    const sentences = strippedText.split(/[.!?]+/).filter((s) => s.trim().length > 0).length
    const paragraphs = strippedText.split(/\n+/).filter((s) => s.trim().length > 0).length
    
    const totalReadingSeconds = (words / 200) * 60
    const minutes = Math.floor(totalReadingSeconds / 60)
    const seconds = Math.round(totalReadingSeconds % 60)
    
    return {
      letters,
      characters,
      words,
      sentences,
      paragraphs,
      readingTime: `${minutes}m ${seconds}s`,
    }
  }, [editedManual])

  return (
    <>
      <h1 className='mt-3 ms-3'>Shopify Manual Writer</h1>
      
      {/* Main Form Card */}
      <div className='card mx-3 mt-4'>
        <div className='card-body'>
          {/* Current Store Info */}
          <div className='mb-3'>
            <div className='d-flex justify-content-between align-items-center mb-2'>
              <label className='form-label mb-0'>Current Store</label>
              {currentStoreName && (
                <button
                  className='btn btn-sm btn-outline-secondary'
                  onClick={fetchBlogStats}
                  disabled={loadingStats}
                  title='Refresh blog stats'
                >
                  <i className={`fa-solid fa-refresh ${loadingStats ? 'fa-spin' : ''}`}></i>
                </button>
              )}
            </div>
            <div className='d-flex align-items-center gap-3'>
              <div className='badge bg-primary fs-6 px-3 py-2'>
                {currentStoreName || 'No store selected'}
              </div>
              {loadingStats ? (
                <span className='text-muted'>Loading stats...</span>
              ) : blogStats ? (
                <div className='d-flex align-items-center gap-3'>
                  <span className={`text-${blogStats.storeInfo.hasBlogConfigured ? 'success' : 'warning'}`}>
                    <i className={`fa-solid fa-${blogStats.storeInfo.hasBlogConfigured ? 'check-circle' : 'exclamation-triangle'}`}></i>
                    {blogStats.storeInfo.hasBlogConfigured ? 'Blog Configured' : 'Blog Not Configured'}
                  </span>
                  {blogStats.storeInfo.hasBlogConfigured && (
                    <span className='text-info'>
                      <i className='fa-solid fa-file-text'></i>
                      {blogStats.blogStats.totalArticles} Manual{blogStats.blogStats.totalArticles !== 1 ? 's' : ''}
                    </span>
                  )}
                  {blogStats.blogStats.error && (
                    <span className='text-danger' title={blogStats.blogStats.error}>
                      <i className='fa-solid fa-exclamation-circle'></i>
                      Error fetching stats
                    </span>
                  )}
                </div>
              ) : currentStoreName ? (
                <span className='text-muted'>Failed to load store info</span>
              ) : null}
            </div>
          </div>

          {/* Product URL Input */}
          <div className='mb-3'>
            <label className='form-label'>
              Shopify Product URL <span className='text-danger'>*</span>
            </label>
            <input
              type='text'
              className='form-control'
              value={productUrl}
              onChange={(e) => setProductUrl(e.target.value)}
              placeholder='Enter Shopify product URL...'
            />
          </div>

          {/* Additional Content */}
          <div className='mb-3'>
            <label className='form-label'>Additional Manual Content (optional)</label>
            <textarea
              className='form-control'
              rows={5}
              value={additionalContent}
              onChange={(e) => setAdditionalContent(e.target.value)}
              placeholder='Enter any additional content to include in the manual...'
            />
          </div>

          {/* AI Options */}
          <AIOptions value={aiOptions} onChange={setAiOptions} />

          {/* Action Buttons */}
          <div className='mb-3 d-flex flex-wrap gap-2'>
            <button
              className='btn btn-primary'
              onClick={handleFetchProduct}
              disabled={fetchingProduct || !productUrl.trim() || !currentStoreName}
            >
              {fetchingProduct ? 'Fetching...' : 'Fetch Product Data'}
            </button>
            
            <button
              className='btn btn-success'
              onClick={() => handleGenerateManual()}
              disabled={generatingManual || !productData}
            >
              {generatingManual ? 'Generating...' : 'Generate Manual'}
            </button>
            
            <button
              className='btn btn-warning'
              onClick={handlePushManual}
              disabled={loading || !editedManual.trim() || !productData}
            >
              {loading ? 'Pushing...' : 'Push Manual to Shopify'}
            </button>
            
            <button
              className='btn btn-outline-secondary'
              onClick={handleReset}
              disabled={loading || fetchingProduct || generatingManual}
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* Product Information Card */}
      {productData && (
        <div className='card mx-3 mt-2'>
          <div className='card-header d-flex justify-content-between align-items-center'>
            <span>Product Information</span>
            {hasExistingManual && (
              <span className='badge bg-warning'>
                Manual Already Exists
                {existingManualUrl && (
                  <a 
                    href={existingManualUrl} 
                    target='_blank' 
                    rel='noopener noreferrer'
                    className='text-white ms-2'
                  >
                    View
                  </a>
                )}
              </span>
            )}
          </div>
          <div className='card-body'>
            <h5>{productData.title}</h5>
            <p><strong>Product ID:</strong> {productData.id}</p>
            <p><strong>Variants:</strong> {productData.variants.length}</p>
            <p><strong>Images:</strong> {productData.images.length}</p>
            {productData.images.length > 0 && (
              <div className='mt-2'>
                <strong>Featured Image:</strong>
                <br />
                <img 
                  src={productData.images[0]} 
                  alt='Product' 
                  style={{ maxWidth: '200px', maxHeight: '200px' }}
                  className='mt-2'
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Content and Preview */}
      <div className='row mx-3 mt-3'>
        {/* Extracted Instructions */}
        <div className='col-md-4 mb-3'>
          <div className='card'>
            <div className='card-header'>Extracted Instructions</div>
            <div className='card-body' style={{ minHeight: '300px' }}>
              {extractingInstructions ? (
                <div className='text-muted'>Extracting instructions...</div>
              ) : extractedInstructions ? (
                <div style={{ whiteSpace: 'pre-wrap' }}>{extractedInstructions}</div>
              ) : (
                <p className='text-muted'>No instructions extracted yet.</p>
              )}
            </div>
          </div>
          
          {/* Manual Stats */}
          {editedManual && (
            <div className='card mt-3'>
              <div className='card-header'>Manual Stats</div>
              <div className='card-body'>
                <p><strong>Letters:</strong> {stats.letters}</p>
                <p><strong>Characters:</strong> {stats.characters}</p>
                <p><strong>Words:</strong> {stats.words}</p>
                <p><strong>Sentences:</strong> {stats.sentences}</p>
                <p><strong>Paragraphs:</strong> {stats.paragraphs}</p>
                <p><strong>Reading Time:</strong> {stats.readingTime}</p>
              </div>
            </div>
          )}
        </div>

        {/* Editable Manual HTML */}
        <div className='col-md-4 mb-3'>
          <div className='card'>
            <div className='card-header'>Editable Manual HTML</div>
            <div className='card-body' style={{ minHeight: '300px' }}>
              {generatingManual ? (
                <div className='text-muted'>Generating manual...</div>
              ) : (
                <textarea
                  className='form-control'
                  style={{ height: '300px', fontFamily: 'monospace', fontSize: '12px' }}
                  value={editedManual}
                  onChange={(e) => setEditedManual(e.target.value)}
                  placeholder='Generated manual HTML will appear here...'
                />
              )}
            </div>
          </div>
        </div>

        {/* Rendered Preview */}
        <div className='col-md-4 mb-3'>
          <div className='card'>
            <div className='card-header'>Manual Preview</div>
            <div className='card-body' style={{ minHeight: '300px', overflowY: 'auto' }}>
              {generatingManual ? (
                <div className='text-muted'>Generating manual...</div>
              ) : editedManual ? (
                <div dangerouslySetInnerHTML={{ __html: editedManual }} />
              ) : (
                <p className='text-muted'>No manual content generated yet.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export const ShopifyManualWriterWrapper: FC = () => {
  return <ManualWriterPage />
}

